import { TemplateQueryContext } from 'routes/dashboard/DashboardPageRoot';
import { Query } from 'system/query/Query';
import { i18n } from "i18next";

// API record for a widget's stored options and data
export class Widget {
  id: string = "";
  uuid: string = "";

  name: string = "";

  user_id: string = "";

  created_at: string = "";
  updated_at: string = "";
  cached_at: string = "";

  // IMPORTANT DEV NOTE!
  // DO NOT READ THIS FOR REFERENCE WITHIN THE WIDGET! ONLY USE THIS TO DEFINE THE REQUESTS IN SETTINGS!
  // The request info can be overridden or created outside of the widget, mainly by the build default requests functions.
  // This defines the prefered way to build requests, but widgets get ActiveWidgetRequests passed to them that represent
  // the actual requests that were run, which may differ based on template queries, user selections, etc.
  // You can access the original request used to create the ActiveWidgetRequest via the following function lower in this file:
  // getSingleOriginalRequestByName(activeWidgetRequests, requestName)
  request_info?: WidgetRequestInfo = {} as WidgetRequestInfo;

  // This should rarely be accessed directly, the widget wrapper functions handle all caching logic
  // Widgets get the same response properties whether that response came from the cache or a live request
  cache?: WidgetCache = {} as WidgetCache;
  // This widget has been saved (cached).  This flag is used as part of the widget render process to prevent extra rendering from React.
  isCached: boolean = false;

  data: any = {};

  _deleted?: boolean = false;

  _deletable?: boolean = true;
  _modifiable?: boolean = true;
  _owned_by_you?: boolean = true;

  _user?: any = {};
  _account?: any = {};

  // constructor for a widget
  constructor(rawWidgetObject: any) {
    Object.assign(this, rawWidgetObject);

    // parse json fields
    if (typeof this.data === "string") {
      this.data = JSON.parse(this.data);
    }
    if (typeof this.cache === "string") {
      this.cache = JSON.parse(this.cache) || {};
    }
  }
}

// Auto settings for multiquery widget and template query behavior.
// These are intended as defaults for multiquery widgets that might
// need different behaviors for template dashboards vs fixed reports.
// If this is anything other than CUSTOM, the widgetTemplateQueryBehavior
// and widgetQueryBehavior settings on the individual requests will be ignored.
export enum AutoWidgetQueryBehavior {

  // If a template dashboard, this is quivalent to template multi always and widget single combined
  // If a fixed report, this is equivalent to template combined and widget multi request
  AUTO_MULTI_ALWAYS = "AUTO_MULTI_ALWAYS",

  // If a template dashboard, this is equivalent to template multi when all and widget single combined
  // If a fixed report, this is equivalent to template single combined and widget multi request
  AUTO_MULTI_WHEN_ALL = "AUTO_MULTI_WHEN_ALL",

  // This is equivalent to template single combined and widget single combined
  AUTO_SINGLE = "AUTO_SINGLE",

  // Not using an "Auto" setting, use the widget and template query behavior settings as defined
  // "undefined" for the widget property is the same as this
  CUSTOM = "CUSTOM"
}

export enum WidgetTemplateQueryBehavior {
  // Always act as a single query widget - use the combined query if "all" is selected - uses "OR" combination
  // Defaults to this
  SINGLE_COMBINED = "SINGLE_COMBINED",
  // Always act as a single query widget - use the intersection of queries if "all" is selected - Uses "AND" combination
  SINGLE_INTERSECTION = "SINGLE_INTERSECTION",
  // Break into separate requests per each template query when all is selected, single otherwise
  MULTI_WHEN_ALL = "MULTI_WHEN_ALL",
  // Always break into separate requests per each template query, even when a single query is selected
  MULTI_ALWAYS = "MULTI_ALWAYS",
  // Do not use template queries
  NONE = "NONE"
}

export enum WidgetQueryBehavior {
  // Default:
  // Use a single widget query, specified by the query index
  // If multiple queries are provided and an index is not specified, queries will be combined with "AND" logic
  SINGLE_INTERSECTION = "SINGLE_INTERSECTION",

  // Use a single widget query, specified by the query index
  // If multiple queries are provided and an index is not specified, queries will be combined with "OR" logic
  SINGLE_COMBINED = "SINGLE_COMBINED",

  // Run once per each widget query
  // This must be used with one of the "SINGLE_*" WidgetTemplateQueryBehavior types or "NONE"
  MULTI_REQUEST = "MULTI_REQUEST",

  // Do not use the widget queries for this request
  NONE = "NONE"
}

export enum WidgetQueryRequirements {
  REQUIRES_WIDGET_QUERIES = "REQUIRES_WIDGET_QUERIES",
  REQUIRES_TEMPLATE_QUERIES = "REQUIRES_TEMPLATE_QUERIES",
  REQUIRES_WIDGET_AND_TEMPLATE_QUERIES = "REQUIRES_WIDGET_AND_TEMPLATE_QUERIES",
}


export interface WidgetRequestInit {
  body?: string | any;
  method?: string;
  query?: string;
}

export interface WidgetRequest {
  // For looking up a specific request for the widget
  name: string;

  // Not including this means the widget will use a combined query
  // using the logic specified by widgetQueryBehavior
  widgetQueryIndex?: number;

  // This should be a full endpoint after the domain, e.g. "query/agg"
  // Any query IDs will be appended to this endpoint based on the above rules, e.g. "query/agg/q_1234,q_asdf"
  // This is the first parameter for the APIRequest call
  endpoint: string;

  // The query color to use for this request in the widget, if applicable
  queryRequestColor?: string;

  // Query parameters to be sent with the request,
  // the second parameter for the APIRequest call
  queryParams?: string | { [key: string]: string | boolean | number | null; };

  // Third parameter for the APIRequest call, generally contains the body, any fixed query modifiers, etc.
  // Using RequestInit directly as a type causes issues, so we define a simpler version above
  requestInit?: WidgetRequestInit;

  // if a request is expanded to multiquery, it will use the following instead
  multiRequestInit?: WidgetRequestInit;

  // Auto settings for multiquery widget and template query behavior.
  // These are intended as defaults for multiquery widgets that might
  // need different behaviors for template dashboards vs fixed reports.
  // If this is anything other than CUSTOM or undefined, the widgetTemplateQueryBehavior
  // and widgetQueryBehavior settings below will be ignored.
  autoWidgetQueryBehavior?: AutoWidgetQueryBehavior;

  // Defaults to SINGLE_INTERSECTION
  // This is ignored if widgetQueryIndex is specified and the index exists
  widgetQueryBehavior?: WidgetQueryBehavior;

  // Defaults to SINGLE_COMBINED
  widgetTemplateQueryBehavior?: WidgetTemplateQueryBehavior;

  // Query requirements for this request, if applicable.
  // This will prevent a request from running if the requirements are not met,
  // In the case that the behavior parameters above are not sufficient to restrict it.
  // Most use cases will not need to set this.
  widgetQueryRequirements?: WidgetQueryRequirements;

  // Optional function to transform the response before it is returned
  // This is a good way to reduce large responses for a smaller cache, or to format the response
  // to a more usable structure for the widget
  responseTransform?: (response: any) => any;
}

export interface ActiveWidgetRequest {
  // For looking up a specific request for the widget
  name: string;

  // The title to display for the query in the widget, if applicable
  // Dynamically created based on the params based on the query (or collected queries ) most likely to be relevant
  queryDisplayTitle?: string;

  // The WidgetRequest that this request was created from
  originalRequest?: WidgetRequest;

  // Query IDs will be appended to the endpoint to create the full endpoint for the APIRequest call
  activeQueryIds: string[];

  // If true, this request can be made with an empty query list,
  // Otherwise it will be discarded if there are no active queries
  allowEmptyQueryList?: boolean;

  // The query color to use for this request in the widget, if applicable
  queryRequestColor?: string;

  // Below are equivalent to params used by the APIRequest call:
  endpoint: string;
  queryParams?: string | { [key: string]: string | boolean | number | null; };
  requestInit?: WidgetRequestInit;
}

export interface WidgetQuery {
  name: string;
  id: string;
}

export interface WidgetRequestInfo {
  queries: WidgetQuery[];
  requests: WidgetRequest[];
}

export interface WidgetResponseItem {
  queryDisplayTitle: string;
  requestName: string;
  originatingRequest: ActiveWidgetRequest;
  runAt: string;
  responseBody: { [key: string]: any };
}

export interface WidgetCache {
  cachedAt: string;
  cacheItems: WidgetResponseItem[][];
  cachedRequests?: Array<ActiveWidgetRequest[] | ActiveWidgetRequest>;
  cachedQueries: Query[];
}

export function getWidgetResponsesByName(apiResponses: Array<WidgetResponseItem[] | WidgetResponseItem> | undefined, requestName: string): WidgetResponseItem[] | undefined {
  if (!apiResponses) {
    return undefined;
  }

  for (const response of apiResponses) {
    if (Array.isArray(response)) {
      const found = response.find(item => item.requestName === requestName);
      if (found) {
        if (response.length > 1) {
          return response;
        } else if (Array.isArray(found)) {
          return found;
        } else if (found) {
          return [found];
        }
      }
    } else if (response.requestName === requestName) {
      return [response];
    }
  }
  return undefined;
}

export function getSingleWidgetResponseByName(apiResponses: Array<WidgetResponseItem[] | WidgetResponseItem> | undefined, requestName: string): WidgetResponseItem | undefined {
  if (!apiResponses) {
    return undefined;
  }

  const response = getWidgetResponsesByName(apiResponses, requestName);
  if (Array.isArray(response)) {
    return response[0];
  }
  return response;
}

export function getActiveWidgetRequestsByName(activeWidgetRequests: Array<ActiveWidgetRequest | ActiveWidgetRequest[]> | undefined, requestName: string): ActiveWidgetRequest | ActiveWidgetRequest[] | undefined {
  if (!activeWidgetRequests) {
    return undefined;
  }

  for (const request of activeWidgetRequests) {
    if (Array.isArray(request)) {
      const found = request.find(item => item.name === requestName);
      if (found) {
        if (request.length > 1) {
          return request;
        } else {
          return found;
        }
      }
    } else if (request.name === requestName) {
      return request;
    }
  }
  return undefined;
}

export function getSingleActiveWidgetRequestByName(activeWidgetRequests: Array<ActiveWidgetRequest | ActiveWidgetRequest[]> | undefined, requestName: string): ActiveWidgetRequest | undefined {
  if (!activeWidgetRequests) {
    return undefined;
  }

  const request = getActiveWidgetRequestsByName(activeWidgetRequests, requestName);
  if (Array.isArray(request)) {
    return request[0];
  }
  return request;
}

export function getSingleOriginalRequestByName(activeWidgetRequests: Array<ActiveWidgetRequest | ActiveWidgetRequest[]> | undefined, requestName: string): WidgetRequest | undefined {
  const request = getSingleActiveWidgetRequestByName(activeWidgetRequests, requestName);
  return request?.originalRequest;
}

export function getOriginalRequestByNameFromResponse(apiResponses: Array<WidgetResponseItem[] | WidgetResponseItem> | undefined, requestName: string): WidgetRequest | undefined {
  const response = getSingleWidgetResponseByName(apiResponses, requestName);
  return response?.originatingRequest?.originalRequest;
}

export function widgetQueryTitleByResponses(apiResponses: Array<WidgetResponseItem[] | WidgetResponseItem> | undefined, requestName: string, i18n: i18n): string {
  const response = getWidgetResponsesByName(apiResponses, requestName);
  if (Array.isArray(response) && response.length > 1) {
    return i18n?.t("widgets.multi-queries");
  } else if (Array.isArray(response) && response.length === 1) {
    return response[0]?.queryDisplayTitle || "";
  } else if (response && response?.queryDisplayTitle) {
    return response?.queryDisplayTitle || "";
  }
  return "";
}

export function overallTemplateQueryTitle(templateQueryContext: TemplateQueryContext, i18n: i18n) : string {
  const atq = templateQueryContext.rawTemplateQueries;
  const stq = templateQueryContext.selectedTemplateQueries;

  const atqLen = atq?.length || 0;
  const stqLen = stq?.length || 0;
  const allIsSelected = stq !== undefined && atq !== undefined &&
        stq.length === atq.length && stq.length > 1 && atqLen > 1;

  if (allIsSelected) {
    return i18n?.t("widgets.all-queries");
  } else if (stqLen > 1) {
    return i18n?.t("widgets.selected-queries");
  } else if (stqLen === 1) {
    return stq[0]?.query?.name;
  }
  return "";
}

export function getSingleResponseValue(response: WidgetResponseItem): number {
  const keys = Object.keys(response.responseBody).filter(key => key !== "_total");
  if (!keys.length) {
    // Records
    return response.responseBody._total;
  }
  if (keys.length > 0) {
    const fieldName = keys[0];
    const field = response.responseBody[fieldName];
    if (typeof field === "number") {
      // Demographics, Social Metrics, Source
      return field;
    } else if (field && typeof field === "object") {
      // Geography, Infegy IQ, Other
      return field?.series?.[0]?.values?.[0] || 0;
    }
  }
  return 0;
}